/**
 * TagDropdownContent Component
 *
 * Multi-select tag picker with search, create-new, confirm and optional remove-all actions.
 * Selection is local until the user confirms — onValueChange is only called on confirm.
 *
 * @example
 * // Action bar placement (no remove button)
 * <TagDropdownContent
 *   value={selectedTagIds}
 *   onValueChange={(ids) => setSelectedTagIds(ids)}
 *   onClose={() => setOpen(false)}
 * />
 *
 * @example
 * // Badge placement (with remove-all button)
 * <TagDropdownContent
 *   placement="badge"
 *   value={task.tagIds}
 *   onValueChange={(ids) => updateTask({ tagIds: ids })}
 *   onClose={() => setOpen(false)}
 *   onRemoveAll={() => updateTask({ tagIds: [] })}
 * />
 */
import React, { useState, useRef } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { showErrorToast } from '@/src/renderer/components/common/MessageToast'
import { Minus, Plus, Trash2 } from 'lucide-react'
import { TagIcon, MultiSelectCheckIcon } from '@/src/renderer/components/icons'
import { Button } from '@/src/renderer/components/ui/Button'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/src/renderer/components/ui/tooltip'
import { useTags } from '../../hooks/useTags'
import { escapeBelongsToTaskDetails } from '../../utils/inlineEscape'
import type { TagDropdownContentProps } from './TagDropdown.types'
import './TagDropdown.scss'

export const TagDropdownContent: React.FC<TagDropdownContentProps> = ({
  value,
  onValueChange,
  onClose,
  onRemoveAll,
  placement = 'actionbar',
  highlightId,
}) => {
  const { t } = useTranslation()
  const { tags, createTag } = useTags()
  const [query, setQuery] = useState('')
  const [localSelected, setLocalSelected] = useState<string[]>(() => [...value])
  const inputRef = useRef<HTMLInputElement>(null)
  const isCreating = useRef(false)
  const inline = placement === 'inline'

  const hasChanges = localSelected.length !== value.length
    || localSelected.some(id => !value.includes(id))

  const availableTags = placement === 'add'
    ? tags.filter((tag) => !value.includes(tag.id))
    : tags

  const filteredTags = availableTags.filter((tag) =>
    tag.name.toLowerCase().includes(query.toLowerCase()),
  )

  const exactMatch = tags.some(
    (tag) => tag.name.toLowerCase() === query.trim().toLowerCase(),
  )
  const showCreateOption = query.trim() !== '' && !exactMatch

  /**
   * Die Auswahl, wie sie zuletzt GESETZT wurde — nicht, wie sie zuletzt gezeichnet wurde.
   *
   * `localSelected` aus der Render-Closure zu lesen ginge zweimal schief: bei zwei Klicks
   * im selben Bild, und in `handleCreate`, das nach einem `await` weiterliest — ein
   * Häkchen, das währenddessen gesetzt wurde, fiele weg.
   *
   * Eine Ref und kein `onValueChange` im Updater: Die App läuft in `React.StrictMode`
   * (`main.tsx`), und React ruft Updater dort doppelt auf, um genau solche Nebeneffekte
   * aufzudecken — zwei Dexie-Schreibvorgänge und zwei Queue-Zeilen je Klick wären die
   * Folge. Dieselbe Falle steht in `ProjectPage` und `NoteNode` beschrieben.
   */
  const selectedRef = useRef<string[]>(localSelected)

  /** Der EINE Ausgang für eine geänderte Auswahl — Ref, Zustand und Meldung in einem. */
  const applySelection = (next: string[]) => {
    selectedRef.current = next
    setLocalSelected(next)
    // In den Aufgaben-Details bleibt der Kasten offen — es gibt keinen
    // Schließen-Moment, an dem eine aufgeschobene Änderung noch ankommen
    // könnte. Also sofort.
    if (inline) onValueChange(next)
  }

  const toggleTag = (id: string) => {
    const prev = selectedRef.current
    applySelection(prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])
  }

  const handleCreate = async () => {
    const trimmed = query.trim()
    if (!trimmed || isCreating.current) return
    isCreating.current = true
    try {
      const newTag = await createTag(trimmed)
      if (placement === 'add') {
        onValueChange([...value, newTag.id])
        onClose()
      } else {
        // Aus der Ref und nicht aus der Closure: Zwischen dem Klick und diesem Punkt lag
        // ein `await`, und ein in der Zwischenzeit gesetztes Häkchen ginge sonst verloren.
        applySelection([...selectedRef.current, newTag.id])
        setQuery('')
      }
    } catch (err) {
      console.error('Failed to create tag:', err)
      showErrorToast(t('taskCreation.tagCreateFailed'))
    } finally {
      isCreating.current = false
    }
  }

  const handleConfirm = () => {
    if (inline) { onValueChange(selectedRef.current); return }
    onClose()
    // Das Popover schließt zuerst; die Änderung kommt eine Zehntelsekunde später,
    // damit die Liste nicht unter dem noch sichtbaren Kasten umspringt.
    setTimeout(() => onValueChange(localSelected), 100)
  }

  const handleRemoveAll = () => {
    if (inline) { onRemoveAll?.(); return }
    onClose()
    setTimeout(() => onRemoveAll?.(), 100)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // In den Aufgaben-Details gehört Escape der Spalte — vor dem
    // `stopPropagation` hinauslassen.
    if (escapeBelongsToTaskDetails(e, inline)) return
    e.stopPropagation()
    if (e.nativeEvent.isComposing) return
    if (e.key === 'Enter') {
      if (showCreateOption) {
        handleCreate()
      } else {
        handleConfirm()
      }
    }
  }

  const dynamicPlaceholder =
    placement === 'add'
      ? (availableTags.length === 0
          ? t('taskCreation.tagCreatePlaceholder')
          : t('taskCreation.tagPlaceholder'))
      : localSelected.length === 0
        ? t('taskCreation.tagPlaceholder')
        : localSelected
            .flatMap((id) => {
              const tag = tags.find((t) => t.id === id)
              return tag ? [`#${tag.name}`] : []
            })
            .join(' ')

  const makePointerDownHandler = (callback: () => void) => (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const btn = e.currentTarget
    let left = false
    const onUp = () => {
      cleanup()
      if (!left) callback()
    }
    const onLeave = () => {
      left = true
      cleanup()
    }
    const cleanup = () => {
      btn.removeEventListener('pointerup', onUp)
      btn.removeEventListener('pointerleave', onLeave)
    }
    btn.addEventListener('pointerup', onUp)
    btn.addEventListener('pointerleave', onLeave)
  }

  return (
    <div className="tag-dropdown" data-placement={placement}>
      <div className="tag-dropdown__search-wrapper">
        <div className="tag-dropdown__search">
          <TagIcon size={16} />
          <input
            ref={inputRef}
            className="tag-dropdown__input"
            placeholder={dynamicPlaceholder}
            value={query}
            // In den Aufgaben-Details stehen sieben Inhalte gleichzeitig offen — sechs
            // von ihnen hätten ein `autoFocus`, und der letzte gewänne. Der Fokus gehört
            // dort der Liste (CONTEXT.md, Aufgaben-Details), bis jemand hineingreift.
            autoFocus={!inline}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>

      <div className="tag-dropdown__list">
        {filteredTags.map((tag) => (
          <button
            key={tag.id}
            type="button"
            className={`context-menu-content__item tag-dropdown__item${(highlightId != null ? tag.id === highlightId : localSelected.includes(tag.id)) ? ' tag-dropdown__item--passive-selected' : ''}`}
            onClick={() => {
              if (placement === 'add' && filteredTags.length <= 1) {
                onValueChange([...value, tag.id])
                onClose()
              } else {
                toggleTag(tag.id)
              }
            }}
          >
            <TagIcon size={16} className="tag-dropdown__item-icon" />
            {tag.name}
            <div className="tag-dropdown__item-right">
              {localSelected.includes(tag.id) ? (
                <>
                  <MultiSelectCheckIcon size={16} className="tag-dropdown__check tag-dropdown__check--default" />
                  <Minus size={16} className="tag-dropdown__check tag-dropdown__check--hover" />
                </>
              ) : (
                <Plus size={16} className="tag-dropdown__add-icon" />
              )}
            </div>
          </button>
        ))}

        {showCreateOption && (
          <button
            type="button"
            className="context-menu-content__item tag-dropdown__item tag-dropdown__item--create"
            onClick={handleCreate}
          >
            <Plus size={14} />
            {t('taskCreation.tagCreateNew', { name: query })}
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {/*
          **Kein Fuß, solange nichts dasteht und nichts angefasst wurde**
          (Nutzer-Vorgabe, 2026-09-19). `badge` zeigte ihn bis dahin immer — im
          Chip der **Aufgaben-Details**, der auch ohne Tags aufgeht, stand
          deshalb ein „Bestätigen" über einer leeren Liste.

          `hasChanges` muss mit hinein: Wer den LETZTEN Tag abwählt, hat nichts
          mehr ausgewählt, braucht den Knopf aber gerade dann — sonst käme das
          Abwählen nie an.

          Dieselbe Regel gilt im **Aufgaben-Menü** (`actionbar`): Der Kasten
          hinter der Menüzeile ist derselbe Kasten, er soll sich nicht anders
          verhalten als der an der **Marke** (angeglichen 2026-09-19).

          Reicht der Aufrufer `onRemoveAll`, gibt es etwas zu entfernen — auch wenn
          die gezeigte Aufgabe keine Tags trägt: die Pille bei Mehrfachauswahl, wo
          eine andere welche hat (#313).
        */}
        {!(showCreateOption && filteredTags.length === 0) && !(placement === 'add' && filteredTags.length <= 1) && (placement === 'add' ? localSelected.some(id => !value.includes(id)) : (localSelected.length > 0 || hasChanges || !!onRemoveAll)) && (
        <motion.div
          key="action-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          <div className="context-menu-content__separator" />
          <div className="tag-dropdown__action-bar">
            <AnimatePresence initial={false} mode="wait">
              {onRemoveAll && !hasChanges ? (
                <motion.button
                  key="remove-full"
                  type="button"
                  className="tag-dropdown__remove-btn tag-dropdown__remove-btn--full"
                  onPointerDown={makePointerDownHandler(handleRemoveAll)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.12 }}
                >
                  <Trash2 size={16} />
                  {t('taskCreation.tagRemoveAll')}
                </motion.button>
              ) : (
                <motion.div
                  key="actions"
                  className="tag-dropdown__action-bar-inner"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.12 }}
                >
                  {onRemoveAll && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" icon={<Trash2 size={16} />} onClick={handleRemoveAll} />
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          {t('taskCreation.tagRemoveAll')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <Button
                    onClick={handleConfirm}
                    shortcut={{ kind: 'return' }}
                    disabled={!onRemoveAll && localSelected.length === 0}
                  >
                    {t('taskCreation.tagConfirm')}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
